import { z } from 'zod'

// Writing Assistant Types

export const WritingAssistantStatusSchema = z.enum([
  'draft',
  'queued',
  'processing',
  'ready',
  'failed',
])

export type WritingAssistantStatus = z.infer<typeof WritingAssistantStatusSchema>

export const VersionHistoryItemSchema = z.object({
  content: z.string(),
  timestamp: z.string(),
})

export const TranslationSchema = z.union([
  z.string(), // Old format
  z.object({
    content: z.string(),
    version: z.number(),
  }),
])

export const WritingAssistantSchema = z.object({
  id: z.string(),
  contentType: z.string(),
  extraInstructions: z.string().nullable(),
  generatedContent: z.string().nullable(),
  status: WritingAssistantStatusSchema,
  versionHistory: z.array(VersionHistoryItemSchema),
  translations: z.record(z.string(), TranslationSchema).nullable().optional().default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
  bobAnalyseId: z.string().nullable().optional(),
  selectedBoodschappen: z.array(z.string()).nullable().optional(),
  createdBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      displayName: z.string().nullable(),
      email: z.string(),
    })
    .optional(),
})

export type WritingAssistant = z.infer<typeof WritingAssistantSchema>

export const WritingAssistantListItemSchema = z.object({
  id: z.string(),
  contentType: z.string(),
  extraInstructions: z.string().nullable(),
  generatedContent: z.string().nullable(),
  status: WritingAssistantStatusSchema,
  versionHistory: z.array(VersionHistoryItemSchema),
  translations: z.record(z.string(), TranslationSchema).nullable().optional().default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
  bobAnalyseId: z.string().nullable().optional(),
  selectedBoodschappen: z.array(z.string()).nullable().optional(),
  createdBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      displayName: z.string().nullable(),
      email: z.string(),
    })
    .optional(),
})

export type WritingAssistantListItem = z.infer<typeof WritingAssistantListItemSchema>

// API Response schemas
export const WritingAssistantResponseSchema = z.object({
  data: z.object({
    id: z.string(),
  }),
})

export const WritingAssistantDetailResponseSchema = z.object({
  data: WritingAssistantSchema,
})

export const WritingAssistantListResponseSchema = z.object({
  data: z.array(WritingAssistantListItemSchema),
})

export const WritingAssistantUpdateResponseSchema = z.object({
  data: z.object({
    writingAssistantId: z.string(),
    updated: z.boolean(),
  }),
})

export const WritingAssistantRegenerateResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    status: z.string(),
    jobId: z.string().optional(),
  }),
})

export const WritingAssistantTranslateResponseSchema = z.object({
  data: z.object({
    writingAssistantId: z.string(),
    targetLanguage: z.string(),
    status: z.string().optional(),
    jobId: z.string().optional(),
    version: z.number().optional(),
    translatedContent: z.string().optional(),
  }),
})

export type WritingAssistantResponse = z.infer<typeof WritingAssistantResponseSchema>
export type WritingAssistantDetailResponse = z.infer<typeof WritingAssistantDetailResponseSchema>
export type WritingAssistantListResponse = z.infer<typeof WritingAssistantListResponseSchema>
export type WritingAssistantUpdateResponse = z.infer<typeof WritingAssistantUpdateResponseSchema>
export type WritingAssistantRegenerateResponse = z.infer<
  typeof WritingAssistantRegenerateResponseSchema
>
export type WritingAssistantTranslateResponse = z.infer<
  typeof WritingAssistantTranslateResponseSchema
>

// Content types
export const CONTENT_TYPES = [
  'Webtekst',
  'Live blog',
  'Twitter bericht',
  'Persbericht',
  'Q&A',
  'Statement van de burgemeester',
] as const

export type ContentType = (typeof CONTENT_TYPES)[number]
